import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';

export const LanguagePicker: React.FC = () => {
  const { i18n, t } = useTranslation('navigation');
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language || 'fr');

  useEffect(() => {
    const handleLangChanged = (lng: string) => {
      const code = (lng || 'fr').split('-')[0];
      setCurrentLang(code);
    };

    i18n.on('languageChanged', handleLangChanged);
    const active = (i18n.language || localStorage.getItem('ekose_rx_locale') || 'fr').split('-')[0];
    setCurrentLang(active);

    return () => {
      i18n.off('languageChanged', handleLangChanged);
    };
  }, [i18n]);

  const handleLanguageChange = (lang: 'fr' | 'en') => {
    i18n.changeLanguage(lang);
    localStorage.setItem('ekose_rx_locale', lang);
    localStorage.setItem('i18nextLng', lang);
    setCurrentLang(lang);
    setIsOpen(false);
  };

  return (
    <div className="relative inline-block text-left z-50">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-800/60 border border-slate-700/50 text-slate-300 hover:text-white hover:bg-slate-800 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500/20 backdrop-blur-sm"
        aria-haspopup="true"
        aria-expanded={isOpen}
        id="language-picker-btn"
      >
        <Globe className="h-4 w-4 text-blue-400" />
        <span className="text-xs font-semibold uppercase">{currentLang}</span>
        <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Overlay to close on click outside */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          
          <div className="absolute right-0 mt-2 w-32 rounded-xl bg-slate-900 border border-slate-800 shadow-xl focus:outline-none z-50 overflow-hidden transform origin-top-right transition-all duration-200 ease-out">
            <div className="py-1" role="menu" aria-orientation="vertical">
              <button
                onClick={() => handleLanguageChange('fr')}
                className={`w-full flex items-center justify-between px-4 py-2 text-xs text-left transition-colors duration-200 ${
                  currentLang === 'fr' 
                    ? 'bg-blue-600/10 text-blue-400 font-semibold' 
                    : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                }`}
                role="menuitem"
              >
                <span>{t('languages.fr')}</span>
                {currentLang === 'fr' && <span className="h-1.5 w-1.5 rounded-full bg-blue-400" />}
              </button>
              <button
                onClick={() => handleLanguageChange('en')}
                className={`w-full flex items-center justify-between px-4 py-2 text-xs text-left transition-colors duration-200 ${
                  currentLang === 'en' 
                    ? 'bg-blue-600/10 text-blue-400 font-semibold' 
                    : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                }`}
                role="menuitem"
              >
                <span>{t('languages.en')}</span>
                {currentLang === 'en' && <span className="h-1.5 w-1.5 rounded-full bg-blue-400" />}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
